import { AlertTriangle, CheckCircle2, Info, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ObsStats, ObsValidationReport } from "@/types/obs";

/**
 * The validator's verdict, rendered for an operator who is about to change what
 * the ministry publishes.
 *
 * The three severities are visually distinct on purpose. An ERROR means a screen
 * would break or render empty and publication is blocked; a WARNING means a real
 * change that needs a human decision (a year removed, ministries dropped); INFO
 * is the diff preview. Collapsing them into one list — the obvious simpler
 * design — would let "12 ministries REMOVED" read like a progress note.
 */

const numberFmt = new Intl.NumberFormat("en-US");

/**
 * One figure in the summary row.
 *
 * 🔑 THE VALUE IS WRAPPED IN `<bdi>`, NOT GIVEN `dir="ltr"`. `dir="ltr"` on the
 * block does two things, and only one of them was wanted: it renders the digits
 * left-to-right (right) AND it left-ALIGNS the block inside an otherwise RTL
 * card (wrong) — so every number sat on the opposite edge from its own label.
 * `<bdi>` isolates the number so its digits and any minus sign still resolve
 * LTR, while the block stays RTL and the value lines up under its label.
 *
 * `break-all` + `min-w-0` are load-bearing too: a grand total here runs to 19
 * digits (2,017,278,899,283,315) and without them it overflowed the card and
 * collided with the neighbouring one. The row is 2-up rather than 4-up for the
 * same reason — four columns inside a 672px dialog cannot fit that number.
 */
function StatCell({
  label,
  value,
  previous,
}: {
  label: string;
  value: string;
  previous?: string;
}) {
  return (
    <div className="min-w-0 rounded-lg border border-border bg-muted/30 p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 break-all font-semibold tabular-nums">
        <bdi>{value}</bdi>
      </div>
      {previous && previous !== value && (
        <div className="mt-0.5 break-all text-xs text-muted-foreground line-through tabular-nums">
          <bdi>{previous}</bdi>
        </div>
      )}
    </div>
  );
}

function Stats({
  stats,
  previous,
}: {
  stats: ObsStats;
  previous?: ObsStats | null;
}) {
  const years = (s: ObsStats) =>
    s.years.length ? `${s.years[0]}–${s.years[s.years.length - 1]}` : "—";
  return (
    <div className="grid grid-cols-2 gap-2">
      <StatCell
        label="عدد السجلات"
        value={numberFmt.format(stats.rows)}
        previous={previous ? numberFmt.format(previous.rows) : undefined}
      />
      <StatCell
        label="السنوات"
        value={years(stats)}
        previous={previous ? years(previous) : undefined}
      />
      <StatCell
        label="عدد الجهات"
        value={numberFmt.format(stats.entities)}
        previous={previous ? numberFmt.format(previous.entities) : undefined}
      />
      <StatCell
        label="إجمالي الفعلي"
        value={numberFmt.format(Math.round(stats.actual))}
        previous={
          previous ? numberFmt.format(Math.round(previous.actual)) : undefined
        }
      />
    </div>
  );
}

function Messages({
  items,
  tone,
}: {
  items: string[];
  tone: "error" | "warning" | "info";
}) {
  if (!items.length) return null;
  const Icon =
    tone === "error" ? XCircle : tone === "warning" ? AlertTriangle : Info;
  return (
    <ul className="space-y-1.5">
      {items.map((message, i) => (
        <li
          key={`${tone}-${i}`}
          className={cn(
            "flex items-start gap-2 rounded-md px-3 py-2 text-sm",
            tone === "error" && "bg-destructive/10 text-destructive",
            tone === "warning" &&
              "bg-amber-500/10 text-amber-700 dark:text-amber-400",
            tone === "info" && "bg-muted/50 text-muted-foreground",
          )}
        >
          <Icon className="mt-0.5 size-4 shrink-0" />
          {/*
            `dir="auto"` per message: the validator mixes Arabic prose with
            column names, sheet names and figures. Forcing the container's RTL
            onto a message that starts with a Latin sheet name pushes its
            punctuation to the wrong end and makes it read as gibberish.
          */}
          <span className="min-w-0 flex-1 break-words" dir="auto">
            {message}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function ObsValidationReportView({
  report,
  previousStats,
}: {
  report: ObsValidationReport;
  previousStats?: ObsStats | null;
}) {
  const blocked = !report.accepted;
  const needsThought = report.accepted && report.warnings.length > 0;

  return (
    <div className="space-y-4">
      <div
        className={cn(
          "flex items-start gap-2 rounded-lg border p-3",
          blocked && "border-destructive/40 bg-destructive/5",
          needsThought && "border-amber-500/40 bg-amber-500/5",
          !blocked && !needsThought && "border-emerald-500/40 bg-emerald-500/5",
        )}
      >
        {blocked ? (
          <XCircle className="mt-0.5 size-5 shrink-0 text-destructive" />
        ) : needsThought ? (
          <AlertTriangle className="mt-0.5 size-5 shrink-0 text-amber-600" />
        ) : (
          <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-emerald-600" />
        )}
        <div className="min-w-0">
          <div className="font-semibold">
            {blocked
              ? "الملف مرفوض — لن يتم نشره"
              : needsThought
                ? "الملف صالح، لكنه يغيّر بيانات منشورة"
                : "الملف صالح وجاهز للنشر"}
          </div>
          <div className="text-sm text-muted-foreground">
            {blocked
              ? "صحّح الملاحظات التالية في ملف Excel ثم أعد رفعه. لم يتغيّر أي شيء على الموقع."
              : needsThought
                ? "راجع التنبيهات أدناه قبل التأكيد."
                : "لم يُعثر على أي مشكلة."}
          </div>
        </div>
      </div>

      {report.stats && <Stats stats={report.stats} previous={previousStats} />}

      <Messages items={report.errors} tone="error" />
      <Messages items={report.warnings} tone="warning" />
      <Messages items={report.info} tone="info" />
    </div>
  );
}
